import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Download, X, Share, SquarePlus, Check } from "lucide-react";

const KEY = "jungle-install-dismissed";
const DAYS14 = 14 * 24 * 60 * 60 * 1000;
const installed = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const dismissedRecently = () => {
  try { return Date.now() - Number(localStorage.getItem(KEY) || 0) < DAYS14; } catch { return false; }
};

const IOSGuide = ({ onClose }) => (
  <div data-testid="ios-install-guide" role="dialog" aria-modal="true" aria-label="Instalar en iPhone" className="fixed inset-0 z-[70] bg-black/60 flex items-end" onClick={onClose}>
    <div className="w-full bg-[#0F3D1E] border-t-2 border-[#8CD05A] rounded-t-[24px] p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] text-white" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between">
        <p className="font-serif-d font-black text-2xl">Instalar en iPhone</p>
        <button type="button" onClick={onClose} data-testid="ios-install-guide-close" aria-label="Cerrar" className="w-11 h-11 grid place-items-center rounded-full bg-white/10"><X className="w-5 h-5" /></button>
      </div>
      <ol className="mt-5 space-y-4">
        {[[Share, "Pulsa Compartir", "el icono del cuadrado con flecha, en la barra de Safari"], [SquarePlus, "Añadir a pantalla de inicio", "desliza hacia abajo en el menú si no lo ves"], [Check, "Pulsa Añadir", "el icono de Jungle aparecerá en tu pantalla de inicio"]].map(([Icon, t, s], i) => (
          <li key={t} className="flex gap-4 items-center">
            <span className="w-11 h-11 shrink-0 rounded-full bg-[#1E7A3C] grid place-items-center font-label text-lg">{i + 1}</span>
            <span className="flex-1"><span className="flex items-center gap-2 font-semibold">{t} <Icon className="w-4 h-4 text-[#BFE3FF]" aria-hidden /></span><span className="block text-sm text-[#E6F2DF]">{s}</span></span>
          </li>
        ))}
      </ol>
    </div>
  </div>
);

export const InstallBanner = () => {
  const { pathname } = useLocation();
  const [prompt, setPrompt] = useState(null);
  const [show, setShow] = useState(false);
  const [guide, setGuide] = useState(false);

  useEffect(() => {
    if (installed() || dismissedRecently()) return undefined;
    const onPrompt = (e) => { e.preventDefault(); setPrompt(e); setShow(true); };
    const onInstalled = () => { setShow(false); setPrompt(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const t = isIOS() ? setTimeout(() => setShow(true), 2500) : null;
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
      if (t) clearTimeout(t);
    };
  }, []);

  const close = () => {
    try { localStorage.setItem(KEY, String(Date.now())); } catch { /* storage blocked */ }
    setShow(false); setGuide(false);
  };
  const install = async () => {
    if (!prompt) { setGuide(true); return; }
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    setPrompt(null);
    if (outcome === "accepted") setShow(false);
  };

  if (!show) return null;
  const bottom = pathname.startsWith("/cuadrante-riego") ? "bottom-[76px]" : "bottom-3";
  return (
    <>
      <div data-testid="install-banner" role="region" aria-label="Instalar la app"
        className={`fixed ${bottom} inset-x-3 sm:left-auto sm:right-4 sm:max-w-md z-[60] rounded-[16px] bg-[#1E7A3C] border border-[#8CD05A]/60 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.6)] text-white flex items-center gap-3 p-3 pl-4`}>
        <img src="/icons/icon-192.png" alt="" className="w-10 h-10 rounded-[10px] shrink-0" />
        <p data-testid="install-banner-text" className="flex-1 text-sm leading-snug">Lleva tu cuadrante de riego siempre contigo. Instala la app gratis</p>
        <button type="button" onClick={install} data-testid="install-banner-button" className="shrink-0 h-10 px-4 rounded-full bg-white text-[#0F3D1E] font-semibold text-sm inline-flex items-center gap-1.5 active:scale-95 transition-transform">
          <Download className="w-4 h-4" aria-hidden /> Instalar
        </button>
        <button type="button" onClick={close} data-testid="install-banner-close" aria-label="Cerrar" className="shrink-0 w-9 h-9 grid place-items-center rounded-full hover:bg-white/10"><X className="w-4 h-4" /></button>
      </div>
      {guide && <IOSGuide onClose={() => setGuide(false)} />}
    </>
  );
};
